import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { DownloadSimple, Scroll, X } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { billingApi, getErrorMessage } from '@/lib/api'
import { useScrollLock } from '@/lib/useScrollLock'
import { cn } from '@/lib/utils'
import { iconChipClass, dangerBannerClass, bannerTextStyle } from '@/lib/styleKit'
import { formatCents } from '@/lib/billingFormat'
import {
  formatInvoiceDate,
  formatInvoicePeriod,
  invoiceAmountCents,
  invoiceDownloadUrl,
  invoiceLabel,
  invoiceStatusChipClass,
  invoiceStatusLabel,
  type Invoice,
  type InvoicePage,
} from '@/lib/invoices'

/**
 * Payment history — every Stripe invoice raised against the account, newest
 * first, each with its PDF.
 *
 * Fetching lives here rather than in `billingStore` on purpose: this is a
 * single-consumer, open-on-demand list that should be fresh each time it opens
 * (an invoice can be paid or voided between visits), and parking it in the
 * store would keep a stale page alive for the life of the session. The store
 * holds what the page renders continuously — plan, credits, usage.
 *
 * The row logic (label, amount, download target, status wording) all lives in
 * `lib/invoices.ts` and is unit-tested there; this file is layout and lifecycle.
 *
 * A11y contract per `design-aesthetics.md` §16/§29/§59, same as
 * `CreditConfirmModal`: portalled to `document.body` at `z-[100]`,
 * `role="dialog"` + `aria-modal`, labelled and described by id, blur-only
 * backdrop as its own `aria-hidden` layer, Escape to dismiss, Tab trapped,
 * focus restored to the trigger on close, background scroll locked.
 */

/** Everything inside the dialog a keyboard user can land on (§16 focus trap). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

const PAGE_SIZE = 24

export interface PaymentHistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function PaymentHistoryModal({ isOpen, onClose }: PaymentHistoryModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  /** Whatever had focus before we opened, so we can hand it back on close. */
  const restoreRef = useRef<HTMLElement | null>(null)
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`

  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useScrollLock(isOpen)

  /**
   * `after` is a Stripe cursor, not an offset — pass the last id on the page to
   * append the next one, omit it to (re)load from the top.
   */
  const load = useCallback(async (after?: string) => {
    const appending = Boolean(after)
    if (appending) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const res = await billingApi.getInvoices({
        limit: PAGE_SIZE,
        ...(after ? { starting_after: after } : {}),
      })
      const page = res.data as InvoicePage
      const rows = page?.invoices ?? []
      setInvoices((prev) => (appending ? [...prev, ...rows] : rows))
      setHasMore(Boolean(page?.has_more))
    } catch (e) {
      setError(getErrorMessage(e, 'Could not load your payment history.'))
      // A failed *append* keeps the rows already on screen; only a failed first
      // page clears, so an error is never shown next to stale rows whose "load
      // more" no longer works.
      if (!appending) {
        setInvoices([])
        setHasMore(false)
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [])

  // Refetch on every open — an invoice's status changes outside this app.
  useEffect(() => {
    if (!isOpen) return
    void load()
  }, [isOpen, load])

  // Focus restore (§16) — captured on open, handed back on close/unmount. Keyed
  // on `isOpen` alone so a re-render for a fetch state can never re-capture a
  // node that lives inside the dialog and is about to be destroyed.
  useEffect(() => {
    if (!isOpen) return
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null
    return () => {
      const previous = restoreRef.current
      restoreRef.current = null
      if (previous?.isConnected && typeof previous.focus === 'function') previous.focus()
    }
  }, [isOpen])

  // Autofocus Close: this dialog is a read-only list, so dismissal is the only
  // affirmative control on it.
  useEffect(() => {
    if (!isOpen) return
    closeRef.current?.focus()
  }, [isOpen])

  // Key handling scoped to the dialog node (§57), never `window` — a listener on
  // window fires alongside the focused button's own Enter activation.
  useEffect(() => {
    const node = dialogRef.current
    if (!isOpen || !node) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        e.preventDefault()
        e.stopPropagation()
        if (focusables.length === 0) return
        const current = focusables.indexOf(document.activeElement as HTMLElement)
        const next =
          current === -1
            ? (e.shiftKey ? focusables[focusables.length - 1] : focusables[0])
            : e.shiftKey
              ? focusables[(current === 0 ? focusables.length : current) - 1]
              : focusables[(current + 1) % focusables.length]
        next?.focus()
      }
    }

    node.addEventListener('keydown', onKey)
    return () => {
      node.removeEventListener('keydown', onKey)
    }
  }, [isOpen, onClose])

  if (!isOpen || typeof document === 'undefined') return null

  const showEmpty = !loading && !error && invoices.length === 0

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
      {/* Backdrop — blur-only, no dark overlay (§16, §45) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={onClose}
        aria-hidden="true"
      />

      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        // Focusable-but-not-tabbable: clicking dead space inside the dialog
        // lands here, so the scoped keydown listener keeps seeing Escape/Tab.
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.98, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
        // 80% of the viewport, as specced — floored on a phone, where 80vw
        // leaves a list too narrow to read, and capped so it does not stretch
        // to an unreadable line length on an ultrawide.
        className={cn(
          'relative flex flex-col w-[80vw] h-[80vh] max-w-[1100px] max-sm:w-[95vw] max-sm:h-[90vh]',
          'rounded-2xl border bg-card shadow-2xl outline-none overflow-hidden',
          'border-border/60 dark:border-white/[0.08] dark:bg-[#191a1b]',
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border/50 dark:border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0">
            <div className={iconChipClass} aria-hidden="true">
              <Scroll className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
            </div>
            <div className="min-w-0">
              <h2
                id={titleId}
                className="text-foreground dark:text-[#f7f8f8]"
                style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.182px' }}
              >
                Payment history
              </h2>
              <p
                id={descId}
                className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                Every charge on this workspace, newest first. Download any invoice as a PDF.
              </p>
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close payment history"
            className={cn(
              'shrink-0 h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors',
              'text-muted-foreground hover:text-foreground hover:bg-muted',
              'dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.06]',
            )}
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        {/* Column headings — hidden at narrow widths, where the rows stack. */}
        <div
          className="hidden sm:grid grid-cols-[minmax(0,1fr)_120px_110px_120px] gap-4 px-6 py-2 border-b border-border/50 dark:border-white/[0.06] text-muted-foreground dark:text-[#8a8f98] uppercase"
          style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
          aria-hidden="true"
        >
          <span>Description</span>
          <span className="text-right">Amount</span>
          <span className="text-center">Status</span>
          <span className="text-right">Invoice</span>
        </div>

        {/* Rows — the only scrolling region, so header and footer stay put. */}
        <div className="flex-1 overflow-y-auto px-6">
          {loading && (
            <>
              <div className="divide-y divide-border/50 dark:divide-white/[0.06]" aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-4">
                    <div className="space-y-2 flex-1">
                      <div className="h-3 w-2/5 rounded bg-muted dark:bg-white/[0.06] animate-pulse" />
                      <div className="h-2.5 w-1/4 rounded bg-muted dark:bg-white/[0.06] animate-pulse" />
                    </div>
                    <div className="h-3 w-20 rounded bg-muted dark:bg-white/[0.06] animate-pulse" />
                  </div>
                ))}
              </div>
              <span className="sr-only" role="status">
                Loading payment history…
              </span>
            </>
          )}

          {error && (
            <div className={cn(dangerBannerClass, 'mt-4')} style={bannerTextStyle} role="alert">
              <span>{error}</span>
            </div>
          )}

          {showEmpty && (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className={iconChipClass} aria-hidden="true">
                <Scroll className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <p
                className="text-foreground dark:text-[#f7f8f8] mt-3"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                No payments yet
              </p>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-1 max-w-sm"
                style={{ fontSize: '12px', lineHeight: 1.55, letterSpacing: '-0.1px' }}
              >
                Invoices appear here once your first subscription charge or credit top-up goes
                through.
              </p>
            </div>
          )}

          {!loading && invoices.length > 0 && (
            <ul className="divide-y divide-border/50 dark:divide-white/[0.06]">
              {invoices.map((invoice) => {
                const href = invoiceDownloadUrl(invoice)
                const period = formatInvoicePeriod(invoice.period_start, invoice.period_end)
                const label = invoiceLabel(invoice)
                return (
                  <li
                    key={invoice.id}
                    className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_120px_110px_120px] gap-2 sm:gap-4 sm:items-center py-4"
                  >
                    <div className="min-w-0">
                      <p
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                        title={label}
                      >
                        {label}
                      </p>
                      <p
                        className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 truncate"
                        style={{ fontSize: '11px', letterSpacing: '-0.08px' }}
                      >
                        {formatInvoiceDate(invoice.created_at)}
                        {' · '}
                        {invoice.number}
                        {period ? ` · ${period}` : ''}
                      </p>
                    </div>

                    <p
                      className="text-foreground dark:text-[#f7f8f8] tabular-nums sm:text-right"
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      {formatCents(invoiceAmountCents(invoice), invoice.currency)}
                    </p>

                    <div className="sm:flex sm:justify-center">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full border px-2 py-0.5 uppercase',
                          invoiceStatusChipClass(invoice.status),
                        )}
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                      >
                        {invoiceStatusLabel(invoice.status)}
                      </span>
                    </div>

                    <div className="sm:flex sm:justify-end">
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 transition-colors',
                            'border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted',
                            'dark:border-white/[0.08] dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.06]',
                          )}
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                          // The invoice number, not just "Download" — a screen
                          // reader listing links otherwise hears the same word
                          // once per row with nothing to tell them apart.
                          aria-label={`Download invoice ${invoice.number}`}
                        >
                          <DownloadSimple className="h-3.5 w-3.5" weight="regular" />
                          Download
                        </a>
                      ) : (
                        <span
                          className="text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '11px', letterSpacing: '-0.08px' }}
                        >
                          Not available
                        </span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}

          {hasMore && !loading && (
            <div className="flex justify-center py-4">
              <Button
                variant="outline"
                size="sm"
                loading={loadingMore}
                onClick={() => void load(invoices[invoices.length - 1]?.id)}
              >
                Load more
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border/50 dark:border-white/[0.06]">
          <p
            className="text-muted-foreground dark:text-[#8a8f98]"
            style={{ fontSize: '11px', letterSpacing: '-0.08px' }}
          >
            Invoices are issued and stored by Stripe.
          </p>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}
